import { pickMimeType } from "./recorder";
import { BARGE_IN_MS, Segmenter, type VadOptions } from "./vad";

/** What one listen produced: an utterance, nothing said before the timeout, or cancelled by the caller. */
export type Capture = { kind: "speech"; audio: Blob; ms: number } | { kind: "silence" } | { kind: "cancelled" };
export interface Pending<T> { done: Promise<T>; cancel(): void }

const TICK_MS = 30;

/**
 * The microphone for a whole hands-free session (CHANGE_voice_level3). It is opened once, from the start tap, and
 * kept open: closing it between turns would switch a Bluetooth car kit between its media and call modes every
 * question, clipping the start of what the agent says. Utterances are recorded from it one at a time; the meter
 * runs on a timer rather than animation frames, which stop when the screen dims.
 */
export class MicSession {
  readonly device: string | null;
  private readonly timer: ReturnType<typeof setInterval>;
  private readonly listeners = new Set<(level: number, t: number) => void>();
  private closed = false;

  private constructor(private readonly stream: MediaStream, private readonly ctx: AudioContext, private readonly analyser: AnalyserNode, private readonly mimeType: string, onLost: () => void) {
    const track = stream.getAudioTracks()[0];
    this.device = track?.label || null;
    track?.addEventListener("ended", () => { if (!this.closed) onLost(); });
    const samples = new Uint8Array(analyser.fftSize);
    this.timer = setInterval(() => {
      if (ctx.state !== "running") return;
      analyser.getByteTimeDomainData(samples);
      let sum = 0;
      for (const s of samples) sum += ((s - 128) / 128) ** 2;
      const level = Math.min(1, Math.sqrt(sum / samples.length) * 4);
      const t = performance.now();
      this.listeners.forEach((f) => f(level, t));
    }, TICK_MS);
  }

  /**
   * Call from the start tap: the analysis context is created and resumed inside the gesture, as iOS requires,
   * before the permission prompt. Throws what getUserMedia throws (NotAllowedError, NotFoundError).
   */
  static async open(onLost: () => void = () => undefined): Promise<MicSession> {
    const ctx = new AudioContext();
    void ctx.resume().catch(() => undefined);
    const mimeType = pickMimeType();
    if (!mimeType) throw new DOMException("no supported recording format", "NotSupportedError");
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch (e) {
      void ctx.close().catch(() => undefined);
      throw e;
    }
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    ctx.createMediaStreamSource(stream).connect(analyser);
    return new MicSession(stream, ctx, analyser, mimeType, onLost);
  }

  /** The context the meter runs in, for callers that watch for an iOS "interrupted" state (a phone call). */
  get audioContext(): AudioContext { return this.ctx; }

  /**
   * Record one utterance: from now until the speaker has been quiet for a while after talking. `timeoutMs` with no
   * speech at all gives "silence". Nothing is recorded that was said before this call.
   */
  capture({ timeoutMs, vad }: { timeoutMs: number; vad?: Partial<VadOptions> }): Pending<Capture> {
    const seg = new Segmenter(vad);
    const rec = new MediaRecorder(this.stream, { mimeType: this.mimeType });
    const parts: Blob[] = [];
    rec.ondataavailable = (e) => { if (e.data.size) parts.push(e.data); };
    let settle: (c: Capture) => void = () => undefined;
    const done = new Promise<Capture>((resolve) => { settle = resolve; });
    let began = 0;
    let finished = false;
    const finish = (outcome: "speech" | "silence" | "cancelled") => {
      if (finished) return;
      finished = true;
      this.listeners.delete(onLevel);
      const ms = Math.round(performance.now() - began);
      rec.onstop = () => settle(outcome === "speech" ? { kind: "speech", audio: new Blob(parts, { type: this.mimeType.split(";")[0] }), ms } : { kind: outcome });
      if (rec.state === "recording") rec.stop(); else settle(outcome === "speech" ? { kind: "speech", audio: new Blob(parts), ms } : { kind: outcome });
    };
    const onLevel = (level: number, t: number) => {
      const ev = seg.push(level, t);
      if (ev === "end" || ev === "cap") finish("speech");
      else if (!seg.started && t - began >= timeoutMs) finish("silence");
    };
    rec.start();
    began = performance.now();
    this.listeners.add(onLevel);
    return { done, cancel: () => finish("cancelled") };
  }

  /** Barge-in: resolves true once someone has talked for BARGE_IN_MS, false if cancelled first. */
  watch(): Pending<boolean> {
    const seg = new Segmenter({ startMs: BARGE_IN_MS });
    let settle: (v: boolean) => void = () => undefined;
    const done = new Promise<boolean>((resolve) => { settle = resolve; });
    const onLevel = (level: number, t: number) => { if (seg.push(level, t) === "speech") { this.listeners.delete(onLevel); settle(true); } };
    this.listeners.add(onLevel);
    return { done, cancel: () => { this.listeners.delete(onLevel); settle(false); } };
  }

  close(): void {
    this.closed = true;
    clearInterval(this.timer);
    this.listeners.clear();
    this.stream.getTracks().forEach((t) => t.stop());
    void this.ctx.close().catch(() => undefined);
  }
}
